import {Component, Input, OnInit} from '@angular/core';
import {NgbActiveModal, NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {EventClassRegistrationResponse, HorseResponse, UserResponse, UserEntityRelationWithUserDataResponse} from '../../../../../../../../../../../../@shared/models/response.module';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {EventsService} from '../../../../../../../../../../../../@shared/services/events-service';
import {Horses} from '../../../../../../../../../../../../@shared/services/horses';
import {ToastService} from '../../../../../../../../../../../../@core/services/toast.service';
import {_AQH_EVE_34, AQH_HOR_0} from '../../../../../../../../../../../../@shared/models/input.module';
import {CompanyService} from '../../../../../../../../../../../../@shared/services/company.service';
import {AddHorse} from '../../../../../../../../../../../../pages/user-profile/components/user-profile-horses/components/add-horse/add-horse';

@Component({
  selector: 'app-edit-registration',
  standalone: false,
  templateUrl: './edit-registration.html',
  styleUrl: './edit-registration.css'
})
export class EditRegistration implements OnInit {
  @Input() registration!: EventClassRegistrationResponse;

  form: FormGroup;
  saving = false;
  loadingHorses = false;
  loadingUsers = false;

  availableHorses: HorseResponse[] = [];
  availableUsers: UserEntityRelationWithUserDataResponse[] = [];
  filteredUsers: UserEntityRelationWithUserDataResponse[] = [];

  selectedUser: UserResponse | null = null;
  selectedHorse: HorseResponse | null = null;

  userSearchTerm = '';

  constructor(
    public activeModal: NgbActiveModal,
    private fb: FormBuilder,
    private eventsService: EventsService,
    private horsesService: Horses,
    private toastService: ToastService,
    private companyService: CompanyService,
    private modalService: NgbModal
  ) {
    this.form = this.fb.group({
      user_id: ['', Validators.required],
      horse_id: [null, Validators.required]
    });
  }

  ngOnInit() {
    if (this.registration) {
      this.selectedUser = this.registration.user || null;
      this.selectedHorse = this.registration.horse || null;

      // Set initial search term
      if (this.selectedUser) {
        this.userSearchTerm = `${this.selectedUser.first_name} ${this.selectedUser.last_name}`;
      }

      // Set user_id immediately
      this.form.patchValue({
        user_id: this.registration.user_id
      });
    }

    // Load all users and horses (horse_id will be set after horses load)
    this.loadUsers();
    this.loadAllHorses();
  }

  loadUsers() {
    this.loadingUsers = true;
    this.companyService.getCompanyEmployees().subscribe({
      next: (users: UserEntityRelationWithUserDataResponse[]) => {
        this.availableUsers = users || [];
        this.filteredUsers = this.availableUsers;
        this.loadingUsers = false;
      },
      error: () => {
        this.loadingUsers = false;
        this.availableUsers = [];
        this.filteredUsers = [];
        this.toastService.error('Failed to load users');
      }
    });
  }

  loadAllHorses() {
    this.loadingHorses = true;
    const payload: AQH_HOR_0 = {};

    this.horsesService.getHorses(payload).subscribe({
      next: (horses: HorseResponse[]) => {
        this.availableHorses = horses || [];
        this.loadingHorses = false;

        // Set the current horse_id after horses are loaded
        if (this.registration && this.registration.horse_id) {
          // Use setTimeout to ensure the component has rendered
          setTimeout(() => {
            this.form.patchValue({
              horse_id: this.registration.horse_id
            }, { emitEvent: true });

            // Update selectedHorse reference
            this.selectedHorse = this.availableHorses.find(h => h.horse_id === this.registration.horse_id) || this.selectedHorse;

            // Manually trigger the form control update
            this.form.get('horse_id')?.updateValueAndValidity();
          }, 100);
        }
      },
      error: () => {
        this.loadingHorses = false;
        this.availableHorses = [];
        this.toastService.error('Failed to load horses');
      }
    });
  }

  onUserSearch() {
    const searchTerm = this.userSearchTerm.toLowerCase().trim();

    if (!searchTerm) {
      this.filteredUsers = this.availableUsers;
      return;
    }

    this.filteredUsers = this.availableUsers.filter(user => {
      const fullName = `${user.first_name} ${user.last_name}`.toLowerCase();
      const email = user.email?.toLowerCase() || '';

      return fullName.includes(searchTerm) || email.includes(searchTerm);
    });
  }

  selectUser(user: UserEntityRelationWithUserDataResponse) {
    this.selectedUser = user as any;
    this.form.patchValue({ user_id: user.user_id });
    this.userSearchTerm = `${user.first_name} ${user.last_name}`;
    this.filteredUsers = [];
  }

  clearUserSearch() {
    this.userSearchTerm = '';
    this.selectedUser = null;
    this.form.patchValue({ user_id: '' });
    this.filteredUsers = [];
  }

  onHorseChanged(horseId: any) {
    this.form.patchValue({ horse_id: horseId });
    this.selectedHorse = this.availableHorses.find(h => h.horse_id === horseId) || null;
  }

  openAddHorseModal() {
    if (!this.selectedUser) {
      this.toastService.warning('Please select a user first');
      return;
    }

    const modalRef = this.modalService.open(AddHorse, { size: 'lg', centered: true });

    modalRef.result.then((newHorse: HorseResponse) => {
      if (newHorse) {
        this.toastService.success('Horse added successfully');
        // Reload all horses to include the newly added horse
        this.loadAllHorses();
      }
    }, () => {
      // Modal dismissed
    });
  }

  save() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.saving = true;

    const payload: _AQH_EVE_34 = {
      class_registration_id: this.registration.class_registration_id,
      user_id: this.form.get('user_id')!.value,
      horse_id: this.form.get('horse_id')!.value
    };

    this.eventsService.updateEventClassRegistration(payload).subscribe({
      next: () => {
        this.saving = false;
        this.toastService.success('Registration updated successfully');
        this.activeModal.close(true);
      },
      error: () => {
        this.saving = false;
        this.toastService.error('Failed to update registration');
      }
    });
  }

  close() {
    this.activeModal.close();
  }

  get userIdInvalid(): boolean {
    const c = this.form.get('user_id');
    return !!(c && c.invalid && c.touched);
  }

  get horseIdInvalid(): boolean {
    const c = this.form.get('horse_id');
    return !!(c && c.invalid && c.touched);
  }
}
